import { BackendError, Service } from '@genial/services';
import { BeneficiariesRequest } from '@genial/services/dist/models';
import { selector, selectorFamily } from 'recoil';

import { BeneficiariesFormData } from '../../../pages/CustomerDetail/components/PrivatePensionContacted/components/ChangeBeneficiaries/constants';

import { customerDetailsEmail, privatePensionSelectedWithCustomerInfo } from './atom';
import {
  normalizePrivatePensionCompanyConfig,
  normalizeCustomerPrivatePensionBeneficiaries,
  normalizeCustomerPrivatePensionContracted,
} from './normalize';
import {
  CustomerPrivatePensionContractedNormalized,
  PrivatePensionCompanyConfigProps,
  PrivatePensionSelectedWithCustomerInfoProps,
} from './types';

export const customerPrivatePensionContractedQuery = selectorFamily<
  { data: CustomerPrivatePensionContractedNormalized[] } | null | undefined,
  { accountId: number }
>({
  key: 'customerPrivatePensionContractedQuery',
  get: ({ accountId }) => {
    return async () => {
      try {
        const { data } = await Service.Pension.V1.getAccountIdByContractsList({
          accountId,
          screenName: 'customerDetails',
        });

        const normalizedData = normalizeCustomerPrivatePensionContracted(data);

        return { data: normalizedData };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [] };
        }

        throw error;
      }
    };
  },
});

export const updateCustomerDetailsEmail = selector<string>({
  key: 'updateCustomerDetailsEmail',
  set: ({ set }, newValue) => {
    const productSelected = newValue;
    return set(customerDetailsEmail, productSelected);
  },
  get: ({ get }) => {
    const item = get(customerDetailsEmail);
    return item;
  },
});

export const updatePrivatePensionSelectedWithCustomerInfo =
  selector<PrivatePensionSelectedWithCustomerInfoProps>({
    key: 'updatePrivatePensionSelectedWithCustomerInfo',
    set: ({ set }, newValue) => {
      const newPrivatePensionAndCustomerInfo = newValue;
      return set(privatePensionSelectedWithCustomerInfo, newPrivatePensionAndCustomerInfo);
    },
    get: ({ get }) => {
      const item = get(privatePensionSelectedWithCustomerInfo);
      return item;
    },
  });

export const customerPrivatePensionBeneficiaries = selectorFamily<
  { data: BeneficiariesFormData['beneficiaries'] } | null | undefined,
  { accountId: number }
>({
  key: 'customerPrivatePensionBeneficiaries',
  get: () => {
    return async () => {
      try {
        const { data } = {
          data: [
            {
              birthDate: new Date(2010, 9, 10),
              percentage: 50,
              relationshipId: 4,
              beneficiaryName: 'John Doe',
            },
            {
              birthDate: new Date(2006, 9, 10),
              percentage: 50,
              relationshipId: 4,
              beneficiaryName: 'Jane Doe 2',
            },
          ] as BeneficiariesRequest[],
        };

        await new Promise((resolve) => setTimeout(resolve, 5000));

        const normalizedData = normalizeCustomerPrivatePensionBeneficiaries(data);

        return { data: normalizedData };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [] };
        }

        throw error;
      }
    };
  },
});

export const privatePensionCompanyConfigQuery = selectorFamily<
  { data: PrivatePensionCompanyConfigProps } | null | undefined,
  { companyName: string }
>({
  key: 'privatePensionCompanyConfigQuery',
  get: ({ companyName }) => {
    return async () => {
      const { data } = await Service.Pension.V1.getCompanyConfig({
        screenName: 'customerDetails',
        companyName,
      });

      const normalizedData = normalizePrivatePensionCompanyConfig(data);

      return { data: normalizedData };
    };
  },
});
